import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";

const primaryTabs = [
  {
    label: "People",
    href: "#people",
    children: [
      { label: "About T.I.I.N.A.", href: "#about" },
      { label: "Our Mahi", href: "#mahi" },
      { label: "Our Team", href: "#team" },
      { label: "Advisors", href: "#advisors" },
    ],
  },
  {
    label: "Place",
    href: "#place",
    children: [
      { label: "Our Training", href: "#training" },
      { label: "Partners", href: "/partners" },
      { label: "Membership", href: "#membership" },
    ],
  },
  {
    label: "Planet",
    href: "#planet",
    children: [
      { label: "Contact", href: "#contact" },
      { label: "Trust & Security", href: "#trust" },
    ],
  },
];

function DropdownTab({ tab, scrolled, onNavigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`nav-${tab.label.toLowerCase()}`}
        className={`flex items-center gap-1 px-4 py-2 text-sm font-bold tracking-wide uppercase transition-colors duration-200 ${
          scrolled
            ? "text-[#bb321a] hover:text-[#364225]"
            : "text-white/90 hover:text-white"
        }`}
      >
        {tab.label}
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 min-w-[200px] bg-white/95 backdrop-blur-xl border border-stone-200 shadow-lg py-1 z-50">
          {/* Primary section link */}
          <a
            href={tab.href}
            onClick={(e) => { onNavigate(e, tab.href); setOpen(false); }}
            className="block px-5 py-2.5 text-xs font-bold tracking-wider uppercase text-[#bb321a] hover:bg-[#FDFBF7] border-b border-stone-100"
          >
            {tab.label}
          </a>
          {tab.children.map((child) => (
            <a
              key={child.href}
              href={child.href}
              data-testid={`nav-${child.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={(e) => { onNavigate(e, child.href); setOpen(false); }}
              className="block px-5 py-2.5 text-sm font-semibold text-[#bb321a] hover:bg-[#FDFBF7] hover:text-[#364225] transition-colors"
            >
              {child.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (e, href) => {
    if (href.startsWith("/")) {
      e.preventDefault();
      setMobileOpen(false);
      window.location.href = href;
      return;
    }
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-stone-200 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex items-center justify-between h-14 sm:h-20 lg:h-24">
        {/* Logo + Primary tabs on the left */}
        <div className="flex items-center gap-1">
          <a
            href="#hero"
            data-testid="nav-logo"
            onClick={(e) => handleNavClick(e, "#hero")}
            className="flex items-center gap-2 flex-shrink-0 mr-4"
          >
            <div className="w-10 h-10 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden cursor-pointer hover:scale-105 transition-transform bg-white shadow-sm">
              <img
                src="/tiina-t-mark.png"
                alt="T.I.I.N.A."
                className="w-[85%] h-[85%] object-contain"
              />
            </div>
          </a>

          {/* Desktop primary tabs */}
          <div className="hidden md:flex items-center gap-0">
            {primaryTabs.map((tab) => (
              <DropdownTab key={tab.label} tab={tab} scrolled={scrolled} onNavigate={handleNavClick} />
            ))}
          </div>
        </div>

        {/* Staff Intranet hard right */}
        <div className="hidden md:flex items-center">
          <a
            data-testid="nav-staff-intranet"
            href="/auth?type=staff"
            className={`px-4 py-2 text-sm font-bold tracking-wide uppercase transition-colors duration-200 ${
              scrolled
                ? "text-[#bb321a] hover:text-[#364225]"
                : "text-white hover:text-white/80"
            }`}
          >
            Staff Intranet
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden p-2"
          style={{ color: scrolled ? "#45443f" : "#FFFFFF" }}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#FDFBF7]/95 backdrop-blur-xl border-b border-stone-200"
        >
          <div className="px-6 py-4 flex flex-col gap-0">
            {primaryTabs.map((tab) => (
              <div key={tab.label}>
                <button
                  onClick={() => setMobileExpanded(mobileExpanded === tab.label ? null : tab.label)}
                  className="w-full flex items-center justify-between py-3 text-sm font-bold tracking-wide uppercase text-[#bb321a] border-b border-stone-100"
                >
                  {tab.label}
                  <ChevronDown size={16} className={`transition-transform ${mobileExpanded === tab.label ? "rotate-180" : ""}`} />
                </button>
                {mobileExpanded === tab.label && (
                  <div className="pl-4 pb-2">
                    <a
                      href={tab.href}
                      onClick={(e) => handleNavClick(e, tab.href)}
                      className="block py-2 text-xs font-bold tracking-wider uppercase text-[#bb321a]"
                    >
                      {tab.label} Section
                    </a>
                    {tab.children.map((child) => (
                      <a
                        key={child.href}
                        href={child.href}
                        onClick={(e) => handleNavClick(e, child.href)}
                        className="block py-2 text-sm font-semibold text-[#57534E] hover:text-[#364225]"
                      >
                        {child.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <a
              href="/auth?type=staff"
              data-testid="mobile-nav-staff-intranet"
              className="py-3 text-sm font-bold tracking-wide uppercase text-[#bb321a] hover:text-[#364225] transition-colors mt-2 border-t border-stone-200"
            >
              Staff Intranet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
